<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type {
  BoardPosition,
  CommandRequest,
  GameState,
  MatchResponse,
  QueueResponse
} from "../../../shared/api/entry-api";
import { EntryAPI, playerIdentity, saveAlias } from "./api";
import {
  applyLocalMove,
  applyLocalPass,
  chooseBotMove,
  localNeighbors,
  localSecondsLeft,
  localViewState,
  newLocalGame,
  resignLocalGame,
  type LocalGameState,
  type LocalMode
} from "./local-game";

interface GoogleCredentialResponse { credential: string; }

interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(options: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }): void;
      renderButton(element: HTMLElement, options: Record<string, string | number | boolean>): void;
    };
  };
}

interface AppleSignInResult { authorization?: { id_token?: string }; }

interface AppleIdentityServices {
  auth: {
    init(options: {
      clientId: string;
      scope: string;
      redirectURI: string;
      state: string;
      nonce: string;
      usePopup: boolean;
    }): void;
    signIn(): Promise<AppleSignInResult>;
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
    AppleID?: AppleIdentityServices;
  }
}

const api = new EntryAPI();
const isLanding = ["entrywordcraft.com", "www.entrywordcraft.com"].includes(window.location.hostname);
const identity = ref(playerIdentity());
const aliasInput = ref(identity.value.alias);
const authProvider = ref(api.provider);
const authBusy = ref<"apple" | "google" | "guest" | null>(null);
const authMessage = ref("");
const googleClientID = (import.meta.env.VITE_GOOGLE_CLIENT_ID || "").trim();
const appleClientID = (import.meta.env.VITE_APPLE_CLIENT_ID || "").trim();
const appleRedirectURI = (import.meta.env.VITE_APPLE_REDIRECT_URI || window.location.origin).trim();
const screen = ref<"home" | "howto" | "multiplayer" | "searching" | "invite" | "game" | "info">("home");
const profileOpen = ref(false);
const match = ref<MatchResponse | null>(null);
const ticket = ref<QueueResponse | null>(null);
const inviteCode = ref("");
const joinCode = ref("");
const message = ref("Build a noun by moving through touching cells.");
const selectedLetter = ref("");
const path = ref<BoardPosition[]>([]);
const placement = ref<BoardPosition | null>(null);
const secondsLeft = ref(60);
const serverOffset = ref(0);
const gameMode = ref<"online" | LocalMode | null>(null);
const localState = ref<LocalGameState | null>(null);
const localNow = ref(Date.now());
const botThinking = ref(false);
const draggedLetter = ref<string | null>(null);
const dragPoint = ref<{ x: number; y: number } | null>(null);
const dragStart = ref<{ x: number; y: number } | null>(null);
const isDraggingLetter = ref(false);
const suppressNextLetterClick = ref(false);
const suppressNextBoardClick = ref(false);
const visibleReaction = ref<string | null>(null);
let reactionTimer: number | undefined;
let botTimer: number | undefined;
let previousStartWord: string | undefined;
let pollTimer: number | undefined;
let clockTimer: number | undefined;

const state = computed<GameState | null>(() => {
  if (gameMode.value === "online") return match.value?.snapshot.gameState ?? null;
  return localState.value ? localViewState(localState.value) : null;
});
const currentRole = computed(() => {
  if (gameMode.value !== "online") return "human";
  const id = api.playerID;
  const map = match.value?.snapshot.playerIDsByRole;
  return map?.human === id ? "human" : map?.guest === id ? "guest" : null;
});
const isYourTurn = computed(() => {
  if (!state.value || state.value.isFinished) return false;
  if (gameMode.value === "passplay") return true;
  return state.value.currentPlayer === currentRole.value && !botThinking.value;
});
const isReady = computed(() => gameMode.value !== "online" || match.value?.snapshot.phase === "active");
const displaySecondsLeft = computed(() => localState.value ? localSecondsLeft(localState.value, localNow.value) : secondsLeft.value);
const displayCurrentPlayer = computed(() => {
  if (!state.value) return "Player";
  if (gameMode.value === "bot" && state.value.currentPlayer === "guest") return "Entry Bot";
  if (gameMode.value === "passplay" && state.value.currentPlayer === "guest") return "Player 2";
  if (gameMode.value === "online") return isYourTurn.value ? "Your turn" : "Opponent turn";
  return identity.value.alias;
});
const displayOpponent = computed(() => {
  if (gameMode.value === "bot") return "Entry Bot";
  if (gameMode.value === "passplay") return "Player 2";
  return match.value?.match.players.find((player) => player.id !== api.playerID)?.alias || "Opponent";
});
const displayOpponentRole = computed(() => gameMode.value === "bot" || gameMode.value === "passplay" ? "guest" : (currentRole.value === "human" ? "guest" : "human"));
const boardCells = computed(() => {
  const board = state.value?.board.rows ?? Array.from({ length: 5 }, () => Array<string | null>(5).fill(null));
  return board.flatMap((row, rowIndex) => row.map((letter, column) => ({ letter, position: { row: rowIndex, column } })));
});
const wordPreview = computed(() => {
  const board = state.value?.board.rows.map((row) => [...row]) ?? Array.from({ length: 5 }, () => Array<string | null>(5).fill(null));
  if (placement.value && selectedLetter.value) board[placement.value.row][placement.value.column] = selectedLetter.value;
  return path.value.map((cell) => board[cell.row][cell.column] || "").join("").toUpperCase();
});
const availablePlacements = computed(() => {
  if (!isYourTurn.value || !selectedLetter.value || placement.value) return new Set<string>();
  return new Set(boardCells.value
    .filter((cell) => !cell.letter && canPlaceLetterAt(cell.position))
    .map((cell) => cellKey(cell.position)));
});
const arrowSegments = computed(() => path.value.slice(1).map((position, index) => ({
  from: path.value[index],
  to: position
})));

function setScreen(next: typeof screen.value): void {
  screen.value = next;
  clearPolling();
  if (next === "game" && gameMode.value === "online") startPolling();
  if (next === "home") {
    clearBotTimer();
    gameMode.value = null;
    localState.value = null;
    match.value = null;
    resetComposer();
  }
}

function openMultiplayer(): void {
  profileOpen.value = false;
  setScreen("multiplayer");
}

function navigateBack(): void {
  profileOpen.value = false;
  if (screen.value === "searching") {
    void stopSearch();
    return;
  }
  if (screen.value === "invite") {
    void cancelInvite();
    return;
  }
  if (screen.value === "game" && gameMode.value === "online" && match.value && !state.value?.isFinished) {
    void leaveOnlineGame();
    return;
  }
  setScreen("home");
}

function resetComposer(): void {
  selectedLetter.value = "";
  path.value = [];
  placement.value = null;
}

function canPlaceLetterAt(position: BoardPosition): boolean {
  const board = state.value?.board.rows;
  if (!board || board[position.row]?.[position.column] !== null) return false;
  return neighbors(position).some((cell) => board[cell.row]?.[cell.column] !== null);
}

function startLocalGame(mode: LocalMode): void {
  clearBotTimer();
  const next = newLocalGame(mode, previousStartWord);
  previousStartWord = next.board.rows[2].join("");
  gameMode.value = mode;
  localState.value = next;
  match.value = null;
  resetComposer();
  message.value = `Center word: ${previousStartWord.toUpperCase()}. ${mode === "bot" ? "Your turn against Entry Bot." : "Player 1 starts."}`;
  setScreen("game");
}

function playAgain(): void {
  if (gameMode.value === "bot" || gameMode.value === "passplay") startLocalGame(gameMode.value);
}

function clearBotTimer(): void {
  if (botTimer !== undefined) window.clearTimeout(botTimer);
  botTimer = undefined;
  botThinking.value = false;
}

function scheduleBotTurn(): void {
  if (gameMode.value !== "bot" || !localState.value || localState.value.isFinished || localState.value.currentPlayer !== "bot" || botThinking.value) return;
  botThinking.value = true;
  botTimer = window.setTimeout(() => {
    const current = localState.value;
    if (!current || current.isFinished || current.currentPlayer !== "bot") {
      botThinking.value = false;
      return;
    }
    const move = chooseBotMove(current);
    if (move) {
      applyLocalMove(current, move, "bot");
      message.value = `Entry Bot played ${move.word.toUpperCase()} (${move.word.length} letters).`;
    } else {
      applyLocalPass(current);
      message.value = "Entry Bot passed. Your turn is ready.";
    }
    botThinking.value = false;
    botTimer = undefined;
    resetComposer();
    localNow.value = Date.now();
  }, 700);
}

function handleLocalTimeout(): void {
  const current = localState.value;
  if (!current || current.isFinished || localSecondsLeft(current, localNow.value) > 0) return;
  if (current.currentPlayer === "bot" && botThinking.value) return;
  const timedOutPlayer = current.currentPlayer;
  applyLocalPass(current);
  resetComposer();
  message.value = current.isFinished
    ? `${timedOutPlayer === "human" ? "You" : timedOutPlayer === "bot" ? "Entry Bot" : "Player 2"} lost after three consecutive passes.`
    : `${timedOutPlayer === "human" ? "Your" : timedOutPlayer === "bot" ? "Entry Bot's" : "Player 2's"} turn timed out.`;
  localNow.value = Date.now();
  scheduleBotTurn();
}

function syncAuthState(): void {
  authProvider.value = api.provider;
  identity.value = playerIdentity();
  aliasInput.value = api.alias;
}

function loadScript(id: string, source: string): Promise<void> {
  const existing = document.getElementById(id);
  if (existing) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = id;
    script.src = source;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("The sign-in service could not be loaded."));
    document.head.appendChild(script);
  });
}

async function configureAuthButtons(): Promise<void> {
  try {
    if (googleClientID) {
      await loadScript("google-identity-services", "https://accounts.google.com/gsi/client");
      window.google?.accounts.id.initialize({
        client_id: googleClientID,
        callback: (response) => { void completeGoogleSignIn(response); },
        auto_select: false,
        cancel_on_tap_outside: true
      });
      const button = document.getElementById("google-signin-button");
      if (button) {
        window.google?.accounts.id.renderButton(button, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          shape: "pill",
          width: 320
        });
      }
    }
    if (appleClientID) {
      await loadScript("apple-sign-in-js", "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js");
    }
  } catch (error) {
    authMessage.value = error instanceof Error ? error.message : "Sign-in is unavailable right now.";
  }
}

async function completeGoogleSignIn(response: GoogleCredentialResponse): Promise<void> {
  if (!response.credential || authBusy.value) return;
  authBusy.value = "google";
  authMessage.value = "";
  try {
    await api.authenticateGoogle(response.credential, aliasInput.value.trim() || undefined);
    syncAuthState();
    message.value = "Signed in with Google.";
  } catch (error) {
    authMessage.value = error instanceof Error ? error.message : "Google sign-in failed.";
  } finally {
    authBusy.value = null;
  }
}

async function signInWithApple(): Promise<void> {
  if (!appleClientID || authBusy.value) {
    authMessage.value = "Apple sign-in is not configured for this site yet.";
    return;
  }
  authBusy.value = "apple";
  authMessage.value = "";
  const rawNonce = crypto.randomUUID();
  try {
    if (!window.AppleID) throw new Error("Apple sign-in is unavailable right now.");
    window.AppleID.auth.init({
      clientId: appleClientID,
      scope: "name email",
      redirectURI: appleRedirectURI,
      state: crypto.randomUUID(),
      nonce: rawNonce,
      usePopup: true
    });
    const result = await window.AppleID.auth.signIn();
    const identityToken = result.authorization?.id_token;
    if (!identityToken) throw new Error("Apple did not return an identity token.");
    await api.authenticateApple(identityToken, rawNonce, aliasInput.value.trim() || undefined);
    syncAuthState();
    message.value = "Signed in with Apple.";
  } catch (error) {
    authMessage.value = error instanceof Error ? error.message : "Apple sign-in failed.";
  } finally {
    authBusy.value = null;
  }
}

async function continueAsGuest(): Promise<void> {
  if (authBusy.value) return;
  authBusy.value = "guest";
  authMessage.value = "";
  try {
    await api.continueAsGuest();
    syncAuthState();
    message.value = "Guest account ready. You can play casual online matches.";
  } catch (error) {
    authMessage.value = error instanceof Error ? error.message : "Guest sign-in failed.";
  } finally {
    authBusy.value = null;
  }
}

function signOut(): void {
  api.signOut();
  syncAuthState();
  message.value = "Signed out. Guest play is still available.";
}

async function deleteAccount(): Promise<void> {
  if (!window.confirm("Delete your Entry account and online data?")) return;
  authBusy.value = "guest";
  authMessage.value = "";
  try {
    await api.deleteAccount();
    syncAuthState();
    message.value = "Your Entry account was deleted.";
  } catch (error) {
    authMessage.value = error instanceof Error ? error.message : "Account deletion failed.";
  } finally {
    authBusy.value = null;
  }
}

function updateClock(response: MatchResponse): void {
  serverOffset.value = Date.parse(response.serverNow) - Date.now();
  refreshClock();
}

function refreshClock(): void {
  const deadline = match.value?.turnDeadlineAt;
  secondsLeft.value = deadline ? Math.max(0, Math.ceil((Date.parse(deadline) - (Date.now() + serverOffset.value)) / 1000)) : 0;
}

function startPolling(): void {
  clearPolling();
  pollTimer = window.setInterval(async () => {
    try {
      if (screen.value === "searching" && ticket.value) {
        const next = await api.queueStatus(ticket.value.ticketID);
        ticket.value = next;
        if (next.matchID) {
          const current = await api.readMatch(next.matchID);
          match.value = current;
          updateClock(current);
          setScreen("game");
        }
      } else if ((screen.value === "invite" || screen.value === "game") && match.value) {
        const current = await api.readMatch(match.value.match.id);
        if (screen.value === "game" && current.latestReaction?.id !== match.value.latestReaction?.id && current.latestReaction?.playerID !== api.playerID) {
          if (current.latestReaction) showReaction(current.latestReaction.reaction);
        }
        match.value = current;
        updateClock(current);
        if (screen.value === "invite" && current.snapshot.phase === "active") setScreen("game");
        if (current.match.status === "cancelled") {
          message.value = "This invite expired or was cancelled.";
          match.value = null;
          setScreen("home");
        }
      }
    } catch (error) {
      message.value = error instanceof Error ? error.message : "Connection failed.";
    }
  }, screen.value === "searching" ? 1000 : 1200);
}

function clearPolling(): void {
  if (pollTimer !== undefined) window.clearInterval(pollTimer);
  pollTimer = undefined;
}

function showReaction(reaction: string): void {
  visibleReaction.value = reaction;
  if (reactionTimer !== undefined) window.clearTimeout(reactionTimer);
  reactionTimer = window.setTimeout(() => {
    visibleReaction.value = null;
    reactionTimer = undefined;
  }, 2200);
}

function reactionText(reaction: string): string {
  return ({ nice: "Nice one", wow: "Whoa", laugh: "That word sent me", ouch: "Ouch" } as Record<string, string>)[reaction] || reaction;
}

function startGameClock(): void {
  if (clockTimer !== undefined) window.clearInterval(clockTimer);
  clockTimer = window.setInterval(() => {
    localNow.value = Date.now();
    if (localState.value && gameMode.value !== "online") {
      handleLocalTimeout();
      scheduleBotTurn();
    } else {
      refreshClock();
    }
  }, 250);
}

async function savePlayerAlias(): Promise<void> {
  try {
    await api.updateAlias(aliasInput.value);
    saveAlias(api.alias);
    syncAuthState();
    message.value = `Playing as ${identity.value.alias}.`;
  } catch (error) {
    message.value = error instanceof Error ? error.message : "Could not save nickname.";
  }
}

async function copyInvite(): Promise<void> {
  if (!inviteCode.value) return;
  await navigator.clipboard?.writeText(inviteCode.value);
  message.value = "Invite code copied.";
}

async function findOpponent(): Promise<void> {
  try {
    gameMode.value = "online";
    ticket.value = await api.findOpponent();
    syncAuthState();
    message.value = "Looking for an opponent. The timer starts only after both players join.";
    setScreen("searching");
    startPolling();
  } catch (error) {
    message.value = error instanceof Error ? error.message : "Could not start matchmaking.";
  }
}

async function stopSearch(): Promise<void> {
  if (ticket.value) await api.cancelSearch(ticket.value.ticketID).catch(() => undefined);
  ticket.value = null;
  message.value = "Search cancelled.";
  setScreen("home");
}

async function cancelInvite(): Promise<void> {
  if (match.value) await api.closeMatch(match.value.match.id).catch(() => undefined);
  match.value = null;
  ticket.value = null;
  message.value = "Invite cancelled.";
  setScreen("home");
}

async function createInvite(): Promise<void> {
  try {
    gameMode.value = "online";
    match.value = await api.createInvite();
    syncAuthState();
    inviteCode.value = match.value.match.inviteCode;
    message.value = "Share this invite code. The match starts when the second player joins.";
    setScreen("invite");
    startPolling();
  } catch (error) {
    message.value = error instanceof Error ? error.message : "Could not create an invite.";
  }
}

async function leaveOnlineGame(): Promise<void> {
  if (!window.confirm("Leave this match? Leaving now counts as a loss.")) return;
  if (match.value && !state.value?.isFinished) {
    await api.command(match.value.match.id, {
      commandID: crypto.randomUUID(),
      expectedRevision: match.value.snapshot.revision,
      action: "resign"
    }).catch(() => undefined);
  }
  setScreen("home");
}

async function joinInvite(): Promise<void> {
  const normalized = joinCode.value.replace(/\D/g, "").slice(0, 6);
  joinCode.value = normalized;
  if (normalized.length !== 6) {
    message.value = "Enter the six-digit invite code.";
    return;
  }
  try {
    gameMode.value = "online";
    match.value = await api.joinMatch(normalized);
    syncAuthState();
    updateClock(match.value);
    setScreen("game");
  } catch (error) {
    message.value = error instanceof Error ? error.message : "Could not join this match.";
  }
}

function selectCell(position: BoardPosition): void {
  if (!isYourTurn.value || state.value?.board.rows[position.row][position.column]) return;
  if (placement.value && placement.value.row === position.row && placement.value.column === position.column) {
    placement.value = null;
    path.value = path.value.filter((cell) => cell.row !== position.row || cell.column !== position.column);
    return;
  }
  if (!canPlaceLetterAt(position)) {
    message.value = "Choose a cell touching an existing letter.";
    return;
  }
  placement.value = position;
  if (!path.value.some((cell) => cell.row === position.row && cell.column === position.column)) path.value.push(position);
}

function selectLetter(letter: string): void {
  if (!isYourTurn.value) return;
  selectedLetter.value = letter.toLowerCase();
}

function tapPath(position: BoardPosition): void {
  if (!isYourTurn.value || !state.value?.board.rows[position.row][position.column]) return;
  if (path.value.some((cell) => cell.row === position.row && cell.column === position.column)) {
    path.value = path.value.filter((cell) => cell.row !== position.row || cell.column !== position.column);
    return;
  }
  const previous = path.value[path.value.length - 1];
  if (!previous || isAdjacent(previous, position)) {
    path.value.push(position);
    return;
  }
  path.value = [position];
  message.value = `Started a new word from ${state.value?.board.rows[position.row][position.column]?.toUpperCase() ?? "this letter"}.`;
}

function beginLetterDrag(event: PointerEvent, letter: string): void {
  if (!isYourTurn.value) return;
  const target = event.currentTarget as HTMLElement | null;
  target?.setPointerCapture(event.pointerId);
  draggedLetter.value = letter.toLowerCase();
  dragStart.value = { x: event.clientX, y: event.clientY };
  dragPoint.value = { x: event.clientX, y: event.clientY };
  isDraggingLetter.value = false;
}

function moveLetterDrag(event: PointerEvent): void {
  if (!draggedLetter.value || !dragStart.value) return;
  dragPoint.value = { x: event.clientX, y: event.clientY };
  if (Math.hypot(event.clientX - dragStart.value.x, event.clientY - dragStart.value.y) >= 10) {
    isDraggingLetter.value = true;
  }
}

function endLetterDrag(event: PointerEvent, letter: string): void {
  if (!draggedLetter.value) return;
  dragPoint.value = { x: event.clientX, y: event.clientY };
  if (isDraggingLetter.value) {
    const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-board-cell]");
    const row = Number(target?.dataset.row);
    const column = Number(target?.dataset.column);
    if (target && Number.isInteger(row) && Number.isInteger(column)) {
      placeLetterAt(letter, { row, column });
      suppressNextBoardClick.value = true;
    } else {
      message.value = "Drop the letter on a highlighted cell.";
    }
    suppressNextLetterClick.value = true;
  }
  draggedLetter.value = null;
  dragPoint.value = null;
  dragStart.value = null;
  isDraggingLetter.value = false;
}

function handleBoardClick(cell: { letter: string | null; position: BoardPosition }): void {
  if (suppressNextBoardClick.value) {
    suppressNextBoardClick.value = false;
    return;
  }
  if (cell.letter) tapPath(cell.position);
  else selectCell(cell.position);
}

function handleLetterClick(letter: string): void {
  if (suppressNextLetterClick.value) {
    suppressNextLetterClick.value = false;
    return;
  }
  selectLetter(letter);
}

function placeLetterAt(letter: string, position: BoardPosition): void {
  if (!isYourTurn.value || !canPlaceLetterAt(position)) {
    message.value = "The letter must go in a highlighted cell touching the board.";
    return;
  }
  if (placement.value) {
    path.value = path.value.filter((cell) => !isSamePosition(cell, placement.value as BoardPosition));
  }
  selectedLetter.value = letter.toLowerCase();
  placement.value = position;
  if (!path.value.some((cell) => isSamePosition(cell, position))) path.value.push(position);
}

function clearMove(): void {
  path.value = [];
  placement.value = null;
  selectedLetter.value = "";
  message.value = "Move cleared. Your turn continues.";
}

async function submitMove(): Promise<void> {
  if (gameMode.value !== "online") {
    submitLocalMove();
    return;
  }
  if (!match.value || !state.value || !isYourTurn.value || !placement.value || !selectedLetter.value || path.value.length === 0) {
    message.value = "Choose a letter, a connected path, and an empty cell.";
    return;
  }
  const command: CommandRequest = {
    commandID: crypto.randomUUID(),
    expectedRevision: match.value.snapshot.revision,
    action: "move",
    move: { letter: selectedLetter.value, placement: placement.value, path: path.value, word: wordPreview.value.toLowerCase() }
  };
  try {
    match.value = await api.command(match.value.match.id, command);
    clearMove();
    message.value = `Submitted ${command.move?.word.toUpperCase()}.`;
    updateClock(match.value);
  } catch (error) {
    message.value = error instanceof Error ? error.message : "The turn was not accepted.";
  }
}

function submitLocalMove(): void {
  const current = localState.value;
  if (!current || current.isFinished || !isYourTurn.value || !placement.value || !selectedLetter.value || path.value.length === 0) {
    message.value = "Choose a letter, a connected path, and an empty cell.";
    return;
  }
  try {
    const playingPlayer = current.currentPlayer;
    const playedWord = wordPreview.value;
    applyLocalMove(current, {
      letter: selectedLetter.value,
      placement: placement.value,
      path: path.value,
      word: playedWord.toLowerCase()
    }, playingPlayer);
    message.value = `${playingPlayer === "guest" ? "Player 2" : identity.value.alias} played ${playedWord}.`;
    resetComposer();
    localNow.value = Date.now();
    scheduleBotTurn();
  } catch (error) {
    message.value = error instanceof Error ? error.message : "The word was not accepted.";
  }
}

async function pass(): Promise<void> {
  if (gameMode.value !== "online") {
    const current = localState.value;
    if (!current || current.isFinished || !isYourTurn.value) return;
    const passingPlayer = current.currentPlayer;
    applyLocalPass(current);
    resetComposer();
    message.value = current.isFinished
      ? `${passingPlayer === "human" ? identity.value.alias : passingPlayer === "bot" ? "Entry Bot" : "Player 2"} lost after three consecutive passes.`
      : `${passingPlayer === "human" ? "You" : passingPlayer === "bot" ? "Entry Bot" : "Player 2"} passed. ${current.currentPlayer === "human" ? "Your turn is ready." : "Player 2's turn is ready."}`;
    localNow.value = Date.now();
    scheduleBotTurn();
    return;
  }
  if (!match.value || !isYourTurn.value) return;
  try {
    match.value = await api.command(match.value.match.id, { commandID: crypto.randomUUID(), expectedRevision: match.value.snapshot.revision, action: "pass" });
    clearMove();
    message.value = "You passed. Waiting for the opponent.";
    updateClock(match.value);
  } catch (error) {
    message.value = error instanceof Error ? error.message : "The pass was not accepted.";
  }
}

async function resign(): Promise<void> {
  if (gameMode.value !== "online") {
    if (localState.value && !localState.value.isFinished) {
      resignLocalGame(localState.value);
      message.value = "You left the game.";
    }
    return;
  }
  if (!match.value) return;
  try {
    match.value = await api.command(match.value.match.id, { commandID: crypto.randomUUID(), expectedRevision: match.value.snapshot.revision, action: "resign" });
    message.value = "You resigned this match.";
    updateClock(match.value);
  } catch (error) {
    message.value = error instanceof Error ? error.message : "Could not leave the match.";
  }
}

async function sendReaction(reaction: string): Promise<void> {
  if (!match.value) return;
  try {
    match.value = await api.command(match.value.match.id, { commandID: crypto.randomUUID(), expectedRevision: match.value.snapshot.revision, action: "reaction", reaction });
    showReaction(reaction);
  } catch (error) {
    message.value = error instanceof Error ? error.message : "Reaction failed.";
  }
}

function neighbors(position: BoardPosition): BoardPosition[] {
  return [{ row: position.row - 1, column: position.column }, { row: position.row + 1, column: position.column }, { row: position.row, column: position.column - 1 }, { row: position.row, column: position.column + 1 }].filter(isInside);
}
function isInside(position: BoardPosition): boolean { return position.row >= 0 && position.row < 5 && position.column >= 0 && position.column < 5; }
function isAdjacent(a: BoardPosition, b: BoardPosition): boolean { return Math.abs(a.row - b.row) + Math.abs(a.column - b.column) === 1; }
function isSamePosition(a: BoardPosition, b: BoardPosition): boolean { return a.row === b.row && a.column === b.column; }
function isSelected(position: BoardPosition): boolean { return path.value.some((cell) => cell.row === position.row && cell.column === position.column); }
function isPlacement(position: BoardPosition): boolean { return placement.value?.row === position.row && placement.value?.column === position.column; }
function cellKey(position: BoardPosition): string { return `${position.row}-${position.column}`; }

onMounted(() => {
  if (isLanding) return;
  syncAuthState();
  startGameClock();
  void configureAuthButtons();
});
onBeforeUnmount(() => { clearPolling(); if (clockTimer !== undefined) window.clearInterval(clockTimer); if (reactionTimer !== undefined) window.clearTimeout(reactionTimer); });
watch(profileOpen, (isOpen) => {
  if (isOpen) void nextTick(() => configureAuthButtons());
});
</script>

<template>
  <main v-if="isLanding" class="landing-shell">
    <nav class="landing-nav" aria-label="Entry">
      <a class="landing-wordmark" href="/" aria-label="Entry home">Entry</a>
      <div class="landing-nav-links">
        <a href="/support.html">Support</a>
      </div>
    </nav>

    <section class="landing-hero">
      <div class="landing-copy">
        <p class="eyebrow">A word strategy game</p>
        <h1>Letters meet.<br /><em>Words emerge.</em></h1>
        <p class="landing-lede">Build English nouns through a living board. Think one move ahead, score more than your opponent, and leave just enough space for the next word.</p>
        <div class="landing-actions">
          <a class="store-button app-store-button" href="https://apps.apple.com/us/app/entry-wordcraft/id6788429776" target="_blank" rel="noreferrer" aria-label="Download Entry on the App Store">
            <span class="store-glyph" aria-hidden="true"></span>
            <span><small>Download on the</small><strong>App Store</strong></span>
          </a>
          <span class="store-button play-store-button" aria-label="Google Play coming soon">
            <span class="store-glyph play-glyph" aria-hidden="true">▶</span>
            <span><small>Google Play</small><strong>Coming soon</strong></span>
          </span>
        </div>
      </div>

      <div class="landing-board-stage" aria-label="Entry game board preview">
        <div class="landing-board-glow" aria-hidden="true"></div>
        <div class="landing-board">
          <div class="landing-cell muted-cell">·</div><div class="landing-cell muted-cell">·</div><div class="landing-cell">S</div><div class="landing-cell">T</div><div class="landing-cell">O</div>
          <div class="landing-cell muted-cell">·</div><div class="landing-cell">N</div><div class="landing-cell filled-cell">E</div><div class="landing-cell active-cell">R</div><div class="landing-cell muted-cell">·</div>
          <div class="landing-cell muted-cell">·</div><div class="landing-cell">A</div><div class="landing-cell active-cell">D</div><div class="landing-cell">·</div><div class="landing-cell muted-cell">·</div>
          <div class="landing-cell muted-cell">·</div><div class="landing-cell">·</div><div class="landing-cell">·</div><div class="landing-cell">·</div><div class="landing-cell">·</div>
          <div class="landing-cell muted-cell">·</div><div class="landing-cell">·</div><div class="landing-cell">·</div><div class="landing-cell">·</div><div class="landing-cell">·</div>
          <span class="landing-path" aria-hidden="true"></span>
        </div>
        <div class="landing-score"><span><small>Your score</small><strong>14</strong></span><i></i><span><small>Opponent</small><strong>11</strong></span></div>
      </div>
    </section>

    <section class="landing-principles" aria-label="Entry features">
      <div><span>01</span><strong>One letter</strong><p>Place a letter beside the board.</p></div>
      <div><span>02</span><strong>One noun</strong><p>Trace a connected word.</p></div>
      <div><span>03</span><strong>One more point</strong><p>Fill the board and outplay.</p></div>
    </section>

    <footer class="landing-footer">
      <span>Entry · Wordcraft</span>
      <div><a href="/privacy.html">Privacy</a><a href="/support.html">Support</a><a href="/account-deletion.html">Account deletion</a></div>
      <span>© 2026 Entry</span>
    </footer>
  </main>

  <main v-else class="app-shell">
    <header v-if="screen !== 'home'" class="topbar">
      <button class="icon-button" aria-label="Back" @click="navigateBack">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg>
      </button>
      <div class="brand" aria-label="Current screen">{{ screen === 'multiplayer' || screen === 'searching' || screen === 'invite' ? 'Multiplayer' : screen === 'howto' ? 'How to Play' : screen === 'info' ? 'Privacy & Support' : 'Entry' }}</div>
      <button class="profile-button" aria-label="Open profile" @click="profileOpen = true">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7 8a7 7 0 0 0-14 0" /></svg>
      </button>
    </header>

    <section v-if="screen === 'home'" class="screen home-screen">
      <button class="home-profile-button" aria-label="Open profile" @click="profileOpen = true">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7 8a7 7 0 0 0-14 0" /></svg>
      </button>
      <div class="entry-mark" aria-label="Entry"><span v-for="(letter, index) in 'Entry'.split('')" :key="letter" :style="{ '--entry-delay': `${index * 55}ms` }">{{ letter }}</span></div>
      <section class="glass-panel home-actions" aria-label="Entry game modes">
        <button class="menu-button primary-menu-button" @click="startLocalGame('bot')">
          <svg class="menu-symbol" viewBox="0 0 24 24" aria-hidden="true"><path d="m8 5 11 7-11 7V5Z" /></svg>
          <strong>Play Bot</strong>
        </button>
        <button class="menu-button" @click="setScreen('howto')">
          <svg class="menu-symbol" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4.5A2.5 2.5 0 0 1 7.5 2H20v17H7.5A2.5 2.5 0 0 0 5 21.5m0-17v17m0-17h13v17H7.5" /></svg>
          <strong>How to Play</strong>
        </button>
        <button class="menu-button" @click="openMultiplayer">
          <svg class="menu-symbol" viewBox="0 0 24 24" aria-hidden="true"><path d="M16 20v-1.5a4.5 4.5 0 0 0-4.5-4.5h-3A4.5 4.5 0 0 0 4 18.5V20m6-10a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7-1a2.5 2.5 0 1 0 0-5m1 11h2v-1a3.5 3.5 0 0 0-3.5-3.5" /></svg>
          <strong>Multiplayer</strong>
        </button>
      </section>
      <section class="glass-panel progress-card">
        <div class="progress-heading"><span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 19V9m7 10V5m7 14v-7" /></svg></span><strong>Your progress</strong><small>This device</small></div>
        <div class="progress-metrics">
          <div><strong>0</strong><small>Score</small></div><i></i><div><strong>0</strong><small>Wins</small></div><i></i><div><strong>0</strong><small>Online</small></div>
        </div>
      </section>
      <button class="support-link" @click="setScreen('info')">
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5m0-8h.01" /></svg>
        Privacy &amp; Support
      </button>
    </section>

    <section v-else-if="screen === 'howto'" class="screen detail-screen">
      <p class="eyebrow">ENTRY RULES</p>
      <h2>How to<br /><em>play</em></h2>
      <div class="glass-panel rule-card">
        <div class="rule-step"><span>1</span><div><strong>Start with five</strong><p>A five-letter English noun is placed across the center of the board.</p></div></div>
        <div class="rule-step"><span>2</span><div><strong>Add one letter</strong><p>Choose an empty cell touching a letter already on the board.</p></div></div>
        <div class="rule-step"><span>3</span><div><strong>Build a noun</strong><p>Trace through touching cells to spell a new English noun, then submit it.</p></div></div>
        <div class="rule-step"><span>4</span><div><strong>Score and fill</strong><p>Longer nouns score more. Fill the board and finish with the highest score.</p></div></div>
        <div class="rule-step"><span>5</span><div><strong>Three passes lose</strong><p>Three consecutive passes by one player end the game.</p></div></div>
      </div>
      <button class="primary-button wide" @click="startLocalGame('bot')">Play Bot <span>→</span></button>
    </section>

    <section v-else-if="screen === 'multiplayer'" class="screen multiplayer-screen">
      <p class="eyebrow">ONLINE PLAY</p>
      <h2>Play with<br /><em>someone</em></h2>
      <p class="lede">Guest play is ready instantly. Sign in from your profile to keep a chosen nickname.</p>
      <section class="glass-panel online-actions">
        <button class="online-action primary-online" @click="findOpponent"><span class="online-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6" /><path d="m16 16 4 4" /></svg></span><span><strong>Find a random opponent</strong><small>Match online</small></span><svg class="chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg></button>
        <button class="online-action" @click="createInvite"><span class="online-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 8a4 4 0 1 0-6 0m-4 12v-1.5A4.5 4.5 0 0 1 9.5 14h1m6-5v6m3-3h-6" /></svg></span><span><strong>Invite a friend</strong><small>Create a private match</small></span><svg class="chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg></button>
        <div class="join-line"><input v-model="joinCode" inputmode="numeric" maxlength="6" placeholder="6-digit invite code" aria-label="6-digit invite code" @keyup.enter="joinInvite" /><button class="secondary-button" @click="joinInvite">Join</button></div>
      </section>
      <button class="secondary-button wide local-button" @click="startLocalGame('passplay')"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="3" width="10" height="18" rx="2" /><path d="M10 6h4" /></svg>Pass &amp; Play</button>
    </section>

    <section v-else-if="screen === 'info'" class="screen detail-screen info-screen">
      <p class="eyebrow">ENTRY</p>
      <h2>Privacy &amp;<br /><em>Support</em></h2>
      <section class="glass-panel info-card"><strong>Rules</strong><p>Start from the five-letter center word. Add one letter next to an existing letter, then trace a connected English noun that includes it.</p></section>
      <section class="glass-panel info-card"><strong>Online play</strong><p>Guest play is available for casual matches. Apple or Google sign-in keeps your chosen nickname and online profile.</p></section>
      <section class="glass-panel info-card"><strong>Privacy</strong><p>No ads or analytics. Solo games stay on this device. Online matches use your player ID, alias, moves, and scores to keep both players in sync.</p></section>
      <section class="glass-panel info-card"><strong>Support</strong><p>Find help, rules, multiplayer guidance, and privacy details on the public Entry pages.</p><div class="info-links"><a href="https://entrywordcraft.com/support.html" target="_blank" rel="noreferrer">Support</a><a href="https://entrywordcraft.com/privacy.html" target="_blank" rel="noreferrer">Privacy</a></div></section>
      <p class="app-version">Entry 1.8</p>
    </section>

    <section v-else-if="screen === 'searching'" class="screen centered-screen">
      <div class="search-orbit"><span></span><span></span><span></span></div>
      <p class="eyebrow">MATCHMAKING</p>
      <h2>Looking for<br /><em>an opponent</em></h2>
      <p class="lede">The server keeps the search alive while you wait. The game timer starts only when both players are ready.</p>
      <button class="secondary-button wide" @click="stopSearch">Stop searching</button>
      <p class="message">{{ message }}</p>
    </section>

    <section v-else-if="screen === 'invite'" class="screen centered-screen">
      <p class="eyebrow">PRIVATE MATCH</p>
      <h2>Invite a<br /><em>friend</em></h2>
      <div class="invite-code">{{ inviteCode }}</div>
      <button class="primary-button wide" @click="copyInvite">Copy invite code</button>
      <p class="lede">Waiting for the second player to join. No turn timer runs before that.</p>
      <button class="secondary-button wide" @click="cancelInvite">Cancel</button>
      <p class="message">{{ message }}</p>
    </section>

    <section v-else class="screen game-screen">
      <div class="game-status glass-panel">
        <div><span class="eyebrow">{{ isReady ? (isYourTurn ? 'YOUR TURN' : `${displayCurrentPlayer.toUpperCase()}`) : 'WAITING' }}</span><strong>{{ displaySecondsLeft ? `0:${String(displaySecondsLeft).padStart(2, '0')}` : '--:--' }}</strong></div>
        <div class="score-line"><span>{{ identity.alias }} {{ state?.scores[currentRole || 'human'] || 0 }}</span><span>{{ displayOpponent }} {{ state?.scores[displayOpponentRole] || 0 }}</span></div>
      </div>

      <div class="board-wrap glass-panel">
        <div class="board" role="grid" aria-label="Entry board">
          <svg class="path-overlay" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <defs><marker id="entry-path-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" /></marker></defs>
            <line v-for="segment in arrowSegments" :key="`${cellKey(segment.from)}-${cellKey(segment.to)}`" :x1="segment.from.column * 20 + 10" :y1="segment.from.row * 20 + 10" :x2="segment.to.column * 20 + 10" :y2="segment.to.row * 20 + 10" marker-end="url(#entry-path-arrow)" />
          </svg>
          <button v-for="cell in boardCells" :key="cellKey(cell.position)" :data-board-cell="true" :data-row="cell.position.row" :data-column="cell.position.column" class="board-cell" :class="{ selected: isSelected(cell.position), placement: isPlacement(cell.position), filled: !!cell.letter, available: availablePlacements.has(cellKey(cell.position)) }" @click="handleBoardClick(cell)">
            <span>{{ cell.letter?.toUpperCase() }}</span>
          </button>
        </div>
        <div class="word-readout"><span>BUILD A WORD</span><strong>{{ wordPreview || 'Choose a cell and a letter' }}</strong></div>
      </div>

      <div class="keyboard glass-panel">
        <button v-for="letter in 'QWERTYUIOPASDFGHJKLZXCVBNM'.split('')" :key="letter" :class="{ chosen: selectedLetter === letter.toLowerCase(), dragging: draggedLetter === letter.toLowerCase() }" @click="handleLetterClick(letter)" @pointerdown="beginLetterDrag($event, letter)" @pointermove="moveLetterDrag" @pointerup="endLetterDrag($event, letter)" @pointercancel="endLetterDrag($event, letter)">{{ letter }}</button>
      </div>

      <div class="game-actions">
        <button class="secondary-button" @click="clearMove">Clear</button>
        <button class="secondary-button" @click="pass">Pass</button>
        <button class="primary-button" @click="submitMove">Submit</button>
      </div>
      <div v-if="gameMode === 'online'" class="reaction-row"><button v-for="reaction in ['nice', 'wow', 'laugh', 'ouch']" :key="reaction" class="reaction-button" @click="sendReaction(reaction)">{{ reaction }}</button></div>
      <button class="quit-button" @click="gameMode === 'online' ? leaveOnlineGame() : resign()">{{ gameMode === 'online' ? 'Leave match' : 'End game' }}</button>
      <p class="message">{{ message }}</p>
      <div v-if="visibleReaction" class="reaction-toast" role="status"><strong>{{ visibleReaction }}</strong><span>{{ reactionText(visibleReaction) }}</span></div>
      <div v-if="state?.isFinished" class="result-panel glass-panel">
        <p class="eyebrow">MATCH COMPLETE</p>
        <h2>{{ state.winner ? (state.winner === currentRole ? 'You win' : 'Opponent wins') : 'Draw' }}</h2>
        <div class="result-actions">
          <button v-if="gameMode !== 'online'" class="primary-button wide" @click="playAgain">Play again</button>
          <button class="secondary-button wide" @click="setScreen('home')">Main menu</button>
        </div>
      </div>
    </section>

    <div v-if="draggedLetter && dragPoint" class="floating-letter" :style="{ left: `${dragPoint.x}px`, top: `${dragPoint.y}px` }" aria-hidden="true">{{ draggedLetter.toUpperCase() }}</div>

    <div v-if="profileOpen" class="sheet-backdrop" @click.self="profileOpen = false">
      <section class="profile-sheet glass-panel" role="dialog" aria-modal="true" aria-labelledby="profile-title">
        <div class="sheet-handle"></div>
        <div class="sheet-heading"><div><p class="eyebrow">ACCOUNT</p><h2 id="profile-title">Your profile</h2></div><button class="sheet-close" aria-label="Close profile" @click="profileOpen = false">×</button></div>
        <p class="auth-status">{{ authProvider === 'guest' ? `Playing as ${api.alias}. Guest play is ready.` : authProvider ? `Signed in with ${authProvider === 'apple' ? 'Apple' : 'Google'}.` : 'Sign in to choose a nickname shown to opponents.' }}</p>
        <div v-if="authProvider === 'apple' || authProvider === 'google'" class="inline-form">
          <input id="alias" v-model="aliasInput" minlength="3" maxlength="24" placeholder="Nickname (3–24 characters)" @change="savePlayerAlias" />
          <button class="secondary-button" @click="savePlayerAlias">Save</button>
        </div>
        <p v-else-if="authProvider === 'guest'" class="auth-hint">Guest names are generated automatically. Sign in to choose your own nickname.</p>
        <div v-if="!authProvider" class="auth-buttons">
          <div v-if="googleClientID" id="google-signin-button" class="google-signin-button"></div>
          <button v-else class="provider-button provider-button-disabled" disabled>Google sign-in needs setup</button>
          <button class="provider-button apple-button" :disabled="authBusy !== null || !appleClientID" @click="signInWithApple">{{ authBusy === 'apple' ? 'Signing in…' : 'Sign in with Apple' }}</button>
          <button class="provider-button guest-button" :disabled="authBusy !== null" @click="continueAsGuest">{{ authBusy === 'guest' ? 'Preparing guest account…' : 'Continue as Guest' }}</button>
        </div>
        <p v-if="!appleClientID" class="auth-hint">Apple sign-in will appear after the Entry Web Services ID is connected.</p>
        <div v-if="authProvider" class="account-actions"><button class="text-button" @click="signOut">Sign Out</button><button class="text-button danger" :disabled="authBusy !== null" @click="deleteAccount">Delete Account</button></div>
        <p v-if="authMessage" class="auth-error" role="status">{{ authMessage }}</p>
        <a class="privacy-link" href="https://entrywordcraft.com/privacy.html" target="_blank" rel="noreferrer">Privacy &amp; Support <span>↗</span></a>
      </section>
    </div>
  </main>
</template>
